/**
 * Weapon source data — scraped, derived, and authored, kept strictly apart.
 *
 * `weapons.data.js` assembles the engine-facing weapon table out of this file. The
 * split exists so that a reader can always answer one question: **did this number
 * come from the reference, or did we make it up?**
 *
 *   `SCRAPED`  — verbatim from kamigame's CoDBO7 wiki, one detail page per weapon.
 *                Every field carries the page it came from. Nothing in here was
 *                adjusted, rounded or "improved". If the site is wrong, this is
 *                wrong in exactly the same way, which is the point.
 *
 *   `DERIVED`  — computed *at module load* from `SCRAPED` by the functions below.
 *                Not typed in. That is deliberate: a derived constant that is typed
 *                in is a derived constant that silently stops matching its source
 *                the first time anyone edits either one.
 *
 *   `AUTHORED` — everything the reference does not publish: reload times, ADS
 *                speeds, recoil magnitudes, spread cones, muzzle velocity,
 *                penetration, ergonomics, fire modes for weapons whose TTK carries
 *                no rate information, and shotgun pellet counts. These are ours.
 *                They are tuned to be internally consistent with the class and with
 *                the derived fire rate, and they are guesses.
 *
 * Scraped: 2026-08-08. Index: https://kamigame.jp/codbo7/page/395985918999154560.html
 *
 * ---------------------------------------------------------------------------
 * How the fire rate is recovered
 * ---------------------------------------------------------------------------
 * The reference publishes damage-by-range and time-to-kill but not RPM. With 100 hp
 * and a flat damage band, the two are the same fact stated twice:
 *
 *     shotsToKill = ceil(100 / damage)
 *     TTK         = (shotsToKill - 1) x 60 / RPM        (the first shot is at t=0)
 *     => RPM      = 60 x (shotsToKill - 1) / TTK
 *
 * Checked against AK-27: 26 damage -> 4 shots; 0.27 s -> 667 RPM, which is an AK.
 * Its published headshot TTK of 0.17 s then falls out of the same model at a x1.5
 * head multiplier (39 damage -> 3 shots -> 0.18 s predicted). See `HEAD_NOTES`.
 */

/* -------------------------------------------------------------------------- */
/* SCRAPED                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Enemy health, and the head hitbox multiplier the AI module applies.
 *
 * Neither is ours: `src/ai/Enemy.js` sets health to 100 and
 * `src/ai/EnemyModel.js` gives the head hitbox `damageScale: 3.0`. They are
 * recorded here because every derivation on this page depends on them, and because
 * the 3.0 is why each weapon carries a `hitboxScale.head` — see `headHitboxScale`.
 */
export const ENEMY_HP = 100;
export const ENEMY_HEAD_HITBOX = 3.0;

const P = (n) => `https://kamigame.jp/wiki/codbo7/page/${n}.html`;

/**
 * One entry per weapon, verbatim.
 *
 *  `ttk`      { body, head } seconds — the 胴撃ちTTK / ヘッショTTK columns.
 *  `bands`    [[startMetres, damage], ...] — the published damage-by-range table.
 *             These are *steps*, not a curve: 26 damage holds all the way to 21 m
 *             and then becomes 24. `bandsToCurve` below turns them into something
 *             the engine's linear interpolator reproduces exactly.
 *  `magSize`  装弾数 / マガジン容量.
 *  `magazines` マガジン数.
 *  `unlock`   0 means 初期アンロック.
 */
export const SCRAPED = {
  /* ------------------------------ assault rifles ----------------------------- */
  ak27: {
    name: 'AK-27', classId: 'ar', rank: 'S+', unlock: 0, page: P('395964907985570475'),
    ttk: { body: 0.27, head: 0.17 }, magSize: 30, magazines: 3,
    bands: [[0, 26], [21, 24], [41, 22]],
  },
  x9maverick: {
    name: 'X9 Maverick', classId: 'ar', rank: 'S+', unlock: 28, page: P('395964907801021099'),
    ttk: { body: 0.18, head: 0.24 }, magSize: 30, magazines: 3,
    bands: [[0, 34], [17, 31], [29, 29], [43, 22]],
  },
  ds20mirage: {
    name: 'DS20 Mirage', classId: 'ar', rank: 'S', unlock: 37, page: P('395964907733912235'),
    ttk: { body: 0.27, head: 0.19 }, magSize: 30, magazines: 3,
    bands: [[0, 27], [20, 23], [44, 21]],
  },
  peacekeepermk1: {
    name: 'Peacekeeper MK1', classId: 'ar', rank: 'S', unlock: 52, page: P('395964907918461611'),
    ttk: { body: 0.29, head: 0.21 }, magSize: 30, magazines: 3,
    bands: [[0, 23], [15, 22], [25, 18], [39, 15]],
  },
  m15mod0: {
    name: 'M15 MOD 0', classId: 'ar', rank: 'A', unlock: 0, page: P('395962760065757591'),
    ttk: { body: 0.32, head: 0.23 }, magSize: 30, magazines: 3,
    bands: [[0, 22], [28, 21], [46, 18]],
  },
  mxr17: {
    name: 'MXR-17', classId: 'ar', rank: 'A', unlock: 16, page: P('395964907851352747'),
    ttk: { body: 0.37, head: 0.24 }, magSize: 25, magazines: 3,
    bands: [[0, 33], [33, 31], [52, 24]],
  },

  /* ------------------------------- submachine guns --------------------------- */
  razor9mm: {
    name: 'Razor 9mm', classId: 'smg', rank: 'S', unlock: 19, page: P('395965832863204428'),
    ttk: { body: 0.24, head: 0.18 }, magSize: 30, magazines: 4,
    bands: [[0, 21], [12, 17], [18, 13], [28, 11]],
  },
  mpc25: {
    name: 'MPC-25', classId: 'smg', rank: 'S', unlock: 55, page: P('395965832745763916'),
    ttk: { body: 0.22, head: 0.24 }, magSize: 30, magazines: 4,
    bands: [[0, 30], [9, 23], [13, 19], [23, 16]],
  },
  rk9: {
    name: 'RK-9', classId: 'smg', rank: 'A', unlock: 7, page: P('395965832980644940'),
    ttk: { body: 0.32, head: 0.21 }, magSize: 30, magazines: 4,
    bands: [[0, 32], [9, 30], [20, 23], [27, 18]],
  },
  dravec45: {
    name: 'Dravec 45', classId: 'smg', rank: 'A', unlock: 34, page: P('395965832796095564'),
    ttk: { body: 0.29, head: 0.18 }, magSize: 36, magazines: 3,
    bands: [[0, 26], [13, 21], [23, 17], [33, 15]],
  },
  ryden45k: {
    name: 'Ryden 45K', classId: 'smg', rank: 'B', unlock: 0, page: P('395965833030976588'),
    ttk: { body: 0.29, head: 0.19 }, magSize: 30, magazines: 4,
    bands: [[0, 22], [10, 21], [17, 15], [29, 13]],
  },
  carbon57: {
    name: 'Carbon 57', classId: 'smg', rank: 'B', unlock: 46, page: P('395965832913536076'),
    ttk: { body: 0.29, head: 0.21 }, magSize: 50, magazines: 3,
    bands: [[0, 22], [15, 18], [22, 15], [30, 13]],
  },

  /* ---------------------------------- shotguns ------------------------------- */
  /**
   * Shotgun damage is published **per pellet**. M10 Breacher settles it: 20 damage
   * against 100 hp cannot be the one-shot kill its 0/0 TTK claims, but eight pellets
   * at 20 is 160 and obviously is. The pellet count itself is not published — see
   * `AUTHORED.weapon.*.pellets`, which is where that guess lives.
   */
  m10breacher: {
    name: 'M10 Breacher', classId: 'sg', rank: 'S', unlock: 0, page: P('395965932956027563'),
    ttk: { body: 0, head: 0 }, magSize: 8, magazines: 4, perPellet: true,
    bands: [[0, 20], [3, 20], [8, 17], [17, 10]],
  },
  echo12: {
    name: 'Echo 12', classId: 'sg', rank: 'A', unlock: 22, page: P('395965933023136427'),
    ttk: { body: 0.8, head: 0.8 }, magSize: 12, magazines: 3, perPellet: true,
    bands: [[0, 11], [3, 11], [10, 11], [21, 6]],
  },
  akita: {
    name: 'Akita', classId: 'sg', rank: 'A', unlock: 40, page: P('395965933073468075'),
    ttk: { body: 0.3, head: 0.3 }, magSize: 12, magazines: 3, perPellet: true,
    bands: [[0, 6], [3, 7], [6, 5], [12, 3]],
  },

  /* ------------------------------ light machine guns ------------------------- */
  xm325: {
    name: 'XM325', classId: 'lmg', rank: 'S', unlock: 31, page: P('395965984243985913'),
    ttk: { body: 0.26, head: 0.18 }, magSize: 60, magazines: 3,
    bands: [[0, 23], [36, 18], [51, 16]],
  },
  mk78: {
    name: 'MK.78', classId: 'lmg', rank: 'A', unlock: 0, page: P('395965984193654265'),
    ttk: { body: 0.29, head: 0.18 }, magSize: 75, magazines: 3,
    bands: [[0, 32], [41, 30], [61, 23]],
  },

  /* ------------------------------ marksman rifles ---------------------------- */
  warden308: {
    name: 'Warden 308', classId: 'mr', rank: 'S+', unlock: 25, page: P('395966015869066836'),
    // The page prints 0 / 0, which on this wiki means "one shot" rather than "no
    // data" — the same 0 the two one-shot snipers carry. It is still 0, so it
    // yields no rate information. See `oneShot` in AUTHORED.
    ttk: { body: 0, head: 0 }, magSize: 8, magazines: 3,
    bands: [[0, 75], [42, 72], [57, 45]],
  },
  m8a1: {
    name: 'M8A1', classId: 'mr', rank: 'S', unlock: 10, page: P('395966015785180756'),
    ttk: { body: 0.19, head: 0.30 }, magSize: 32, magazines: 3,
    bands: [[0, 30], [36, 29], [48, 24]],
  },
  m34novaline: {
    name: 'M34 Novaline', classId: 'mr', rank: 'A', unlock: 49, page: P('395966015936175700'),
    ttk: { body: 0.11, head: 0.27 }, magSize: 20, magazines: 3,
    bands: [[0, 50], [66, 40]],
  },

  /* ------------------------------- sniper rifles ----------------------------- */
  vsrecon: {
    name: 'VS Recon', classId: 'sr', rank: 'S', unlock: 0, page: P('395966041890500089'),
    ttk: { body: 0.05, head: 0 }, magSize: 6, magazines: 4,
    bands: [[0, 104], [64, 102], [89, 95]],
  },
  shadowsk: {
    name: 'Shadow SK', classId: 'sr', rank: 'A', unlock: 13, page: P('395966041773059577'),
    // The published table starts its first band at 5 m, not 0. Held flat below it.
    ttk: { body: 0, head: 0 }, magSize: 10, magazines: 3,
    bands: [[0, 104], [5, 104], [51, 102], [76, 98]],
  },
  xr3ion: {
    name: 'XR-3 Ion', classId: 'sr', rank: 'B', unlock: 43, page: P('395966041840168441'),
    ttk: { body: 0.05, head: 0 }, magSize: 15, magazines: 4,
    bands: [[0, 90], [102, 90]],
  },
};

/** Class metadata, in roster order. */
export const WEAPON_CLASSES = [
  { id: 'ar', name: 'ASSAULT RIFLE' },
  { id: 'smg', name: 'SUBMACHINE GUN' },
  { id: 'lmg', name: 'LIGHT MACHINE GUN' },
  { id: 'sg', name: 'SHOTGUN' },
  { id: 'mr', name: 'MARKSMAN RIFLE' },
  { id: 'sr', name: 'SNIPER RIFLE' },
];

/* -------------------------------------------------------------------------- */
/* derivation                                                                  */
/* -------------------------------------------------------------------------- */

/** Rounds needed to drop a full-health enemy with `dmg` per round. */
export function shotsToKill(dmg, hp = ENEMY_HP) {
  return Math.ceil(hp / Math.max(1e-6, dmg));
}

/**
 * Fire rate implied by a published damage figure and time-to-kill.
 * Returns null when the TTK carries no rate information — a one-shot kill (TTK 0)
 * or a single-round kill for any other reason.
 */
export function deriveRpm(dmg, ttkBody, hp = ENEMY_HP) {
  const stk = shotsToKill(dmg, hp);
  if (stk <= 1 || !(ttkBody > 0)) return null;
  return (60 * (stk - 1)) / ttkBody;
}

/**
 * Turn the published step bands into a curve the engine's linear interpolator
 * reproduces exactly at every published distance.
 *
 * `damageAtRange` interpolates linearly between control points, so feeding it the
 * raw bands ([[0,26],[21,24]]) would bleed 26 down to 24 across the whole first
 * band — 24.6 damage at 15 m where the source says 26, which is enough to change
 * shots-to-kill. Each band is therefore held flat to its own end and then ramped
 * over `ramp` metres into the next one. The published value is exact everywhere
 * except inside those 2 m seams, where a step would otherwise sit.
 */
export function bandsToCurve(bands, ramp = 2, far = 200) {
  const pts = [];
  // Control points must be strictly increasing in distance or the interpolator
  // divides by a zero-width segment. Two published bands closer together than the
  // ramp collapse the seam to a step, which is correct — there was no room for a
  // ramp there in the first place.
  const push = (x, v) => {
    const last = pts[pts.length - 1];
    pts.push([last && x <= last[0] ? last[0] + 1e-3 : x, v]);
  };

  push(bands[0][0], bands[0][1]);
  for (let i = 1; i < bands.length; i++) {
    const [d, v] = bands[i];
    // Hold the outgoing band's damage until `ramp` metres short of the boundary,
    // then arrive at the incoming one exactly *on* it. Ramping the other way (from
    // the boundary outwards) would mean the published figure for a band was wrong
    // for the first two metres of that band, which is the half that matters.
    push(d - ramp, bands[i - 1][1]);
    push(d, v);
  }
  push(far, bands[bands.length - 1][1]);
  return pts;
}

/* -------------------------------------------------------------------------- */
/* headshot reconciliation                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Per-weapon headshot damage multiplier, and why.
 *
 * The engine's head hitbox is x3.0 (`src/ai/EnemyModel.js`, not ours to change),
 * which is far more than the reference's TTK tables imply. Each weapon therefore
 * carries `hitboxScale.head = mult / 3.0`, so the *effective* head multiplier in
 * play is the one listed here. `headHitboxScale` below does that division.
 *
 * x1.5 is the class default and it reproduces the published headshot TTK for 11 of
 * the 16 weapons whose TTK carries rate information. The exceptions:
 *
 *  - `peacekeepermk1` and `xm325` need a multiplier in [1.09, 1.45) to land on the
 *    published 4-shot / 4-shot headshot kill; x1.5 would make it 3 shots and come
 *    in ~65 ms early. Solved: x1.3.
 *
 *  - `x9maverick`, `mpc25`, `m8a1`, `m34novaline` publish a headshot TTK *longer*
 *    than their bodyshot TTK. That is arithmetically impossible: at one fire rate,
 *    a kill in fewer-or-equal shots cannot take longer. Solving for the multiplier
 *    anyway gives 0.74-0.98, 0.83-1.11, 0.56-0.67 and 0.67-1.00 respectively —
 *    i.e. the source is only self-consistent if headshots did *less* damage than
 *    body shots. We do not ship that. These four keep x1.5 and their measured
 *    headshot TTK will not match the published figure; that is a source defect,
 *    reported rather than hidden.
 *
 *    Worth noting what those four have in common: each is exactly a "one burst,
 *    one kill" weapon — 34x3=102, 30x4=120, 30x4=120, 50x2=100 — which is why they
 *    are the four modelled as burst-fire below. The published bodyshot TTK is the
 *    length of that single burst and matches perfectly; only the headshot column
 *    misbehaves.
 */
export const HEAD_MULT = {
  ak27: 1.5, x9maverick: 1.5, ds20mirage: 1.5, peacekeepermk1: 1.3, m15mod0: 1.5, mxr17: 1.5,
  razor9mm: 1.5, mpc25: 1.5, rk9: 1.5, dravec45: 1.5, ryden45k: 1.5, carbon57: 1.5,
  xm325: 1.3, mk78: 1.5,
  // Shotguns: the published headshot TTK equals the bodyshot TTK, i.e. a headshot
  // does not save a shell. With 8 pellets at 11 that caps the multiplier at 1.136
  // for Echo 12, so it gets 1.065 — which is defensible on its own terms, since
  // only a fraction of a pellet pattern can be inside a head at any range.
  m10breacher: 1.5, echo12: 1.065, akita: 1.5,
  warden308: 1.5, m8a1: 1.5, m34novaline: 1.5,
  vsrecon: 1.5, shadowsk: 1.5, xr3ion: 1.5,
};

/** The `hitboxScale.head` a weapon needs to land on its `HEAD_MULT` in play. */
export function headHitboxScale(id) {
  return (HEAD_MULT[id] ?? 1.5) / ENEMY_HEAD_HITBOX;
}

/* -------------------------------------------------------------------------- */
/* AUTHORED                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Class archetypes. Every weapon starts from its class's block and overrides only
 * what makes it itself, so a rifle cannot accidentally end up with a sniper's
 * sprint-out and the family resemblance inside a class is structural.
 *
 * `bloomGain` is the one field here that is not a raw value. It is the *net* cone
 * growth per shot after that shot's worth of decay, and `weapons.data.js` turns it
 * into `spread.perShot` with the weapon's own fire rate:
 *
 *     perShot = bloomDecay x (60 / rpm) + bloomGain
 *
 * Authoring the net rather than the gross is not a style choice. Bloom that adds
 * less per shot than it decays between shots is pinned at zero and does nothing at
 * all, which is a bug that looks exactly like a design decision — and it shipped
 * here once already. Expressed this way a positive `bloomGain` cannot produce it,
 * whatever the fire rate turns out to be.
 */
export const CLASS_BASE = {
  ar: {
    fireMode: 'auto', pellets: 1, chambered: true, projectile: false,
    reload: { tactical: 2.05, empty: 2.78, magOut: 0.30, magIn: 0.60, boltRelease: 0.80 },
    ads: { in: 0.235, out: 0.19, fovScale: 0.70, sensScale: 0.72 },
    spread: {
      ads: 0, adsMoving: 0.0085, hip: 0.040, hipMoving: 0.070, hipSprint: 0.125,
      crouchScale: 0.72, airScale: 2.4, maxBloom: 0.030, bloomDecay: 0.025, bloomGain: 0.0014,
    },
    recoil: { random: 0.16, kick: 0.13, recovery: 9.0, adsScale: 0.78, crouchScale: 0.85 },
    muzzleVelocity: 880, penetration: 0.95,
    ergonomics: { sprintOut: 0.20, swayScale: 1.0, adsMoveScale: 0.86, switchIn: 0.46, switchOut: 0.30, inspect: 2.6 },
    viewKick: { back: 0.026, up: 0.052, roll: 0.030, side: 0.008, snap: 62, damp: 15 },
    boltTravel: 0.030, shell: { size: 0.9, speed: 3.0 },
    audio: { fire: 'fire_ar', tail: 'tail_ar', dry: 'dryfire' },
    modelFallback: 'ar',
  },
  smg: {
    fireMode: 'auto', pellets: 1, chambered: true, projectile: false,
    reload: { tactical: 1.72, empty: 2.34, magOut: 0.28, magIn: 0.58, boltRelease: 0.78 },
    ads: { in: 0.185, out: 0.155, fovScale: 0.76, sensScale: 0.80 },
    spread: {
      ads: 0, adsMoving: 0.0068, hip: 0.030, hipMoving: 0.052, hipSprint: 0.100,
      crouchScale: 0.70, airScale: 2.2, maxBloom: 0.026, bloomDecay: 0.026, bloomGain: 0.0012,
    },
    recoil: { random: 0.20, kick: 0.095, recovery: 10.5, adsScale: 0.80, crouchScale: 0.85 },
    muzzleVelocity: 400, penetration: 0.45,
    ergonomics: { sprintOut: 0.155, swayScale: 0.82, adsMoveScale: 0.94, switchIn: 0.38, switchOut: 0.24, inspect: 2.3 },
    viewKick: { back: 0.019, up: 0.042, roll: 0.036, side: 0.011, snap: 74, damp: 17 },
    boltTravel: 0.026, shell: { size: 0.75, speed: 2.6 },
    audio: { fire: 'fire_smg', tail: 'tail_smg', dry: 'dryfire' },
    modelFallback: 'smg',
  },
  lmg: {
    fireMode: 'auto', pellets: 1, chambered: true, projectile: false,
    reload: { tactical: 4.40, empty: 5.45, magOut: 0.30, magIn: 0.62, boltRelease: 0.86 },
    ads: { in: 0.43, out: 0.31, fovScale: 0.78, sensScale: 0.82 },
    spread: {
      ads: 0, adsMoving: 0.0125, hip: 0.062, hipMoving: 0.105, hipSprint: 0.165,
      crouchScale: 0.60, airScale: 3.0, maxBloom: 0.034, bloomDecay: 0.020, bloomGain: 0.0007,
    },
    recoil: { random: 0.15, kick: 0.20, recovery: 6.5, adsScale: 0.74, crouchScale: 0.72 },
    muzzleVelocity: 915, penetration: 1.55,
    ergonomics: { sprintOut: 0.34, swayScale: 1.25, adsMoveScale: 0.62, switchIn: 0.72, switchOut: 0.46, inspect: 3.2 },
    viewKick: { back: 0.030, up: 0.058, roll: 0.026, side: 0.009, snap: 54, damp: 13 },
    boltTravel: 0.036, shell: { size: 1.0, speed: 3.2 },
    audio: { fire: 'fire_ar', tail: 'tail_ar', dry: 'dryfire', pitchBase: 0.86 },
    modelFallback: 'lmg',
  },
  sg: {
    fireMode: 'pump', chambered: false, shellByShell: true, projectile: false,
    reload: { tactical: 0.52, empty: 0.52, magOut: 0.18, magIn: 0.55, boltRelease: 0.85, start: 0.34, end: 0.36 },
    ads: { in: 0.24, out: 0.20, fovScale: 0.82, sensScale: 0.86 },
    spread: {
      // Zero, like every other weapon: the *aim* cone of a fully-shouldered,
      // stationary shotgun is exactly zero. The pattern a shell throws is a
      // property of the shell, not of the shooter, and lives in `pelletSpread`.
      ads: 0, adsMoving: 0.010, hip: 0.058, hipMoving: 0.078, hipSprint: 0.120,
      crouchScale: 0.85, airScale: 1.4, maxBloom: 0, bloomDecay: 0.2, bloomGain: 0,
    },
    recoil: { random: 0.14, kick: 0.38, recovery: 5.4, adsScale: 0.82, crouchScale: 0.86 },
    muzzleVelocity: 400, penetration: 0.30,
    ergonomics: { sprintOut: 0.24, swayScale: 1.1, adsMoveScale: 0.88, switchIn: 0.52, switchOut: 0.34, inspect: 2.8 },
    viewKick: { back: 0.062, up: 0.135, roll: 0.018, side: 0.005, snap: 40, damp: 9.5 },
    boltTravel: 0, pumpTravel: 0.075, shell: { size: 1.5, speed: 2.2, delay: 0.28 },
    audio: { fire: 'fire_shotgun', tail: 'tail_shotgun', dry: 'dryfire' },
    modelFallback: 'shotgun',
  },
  mr: {
    fireMode: 'semi', pellets: 1, chambered: true, projectile: true,
    reload: { tactical: 2.35, empty: 3.05, magOut: 0.30, magIn: 0.58, boltRelease: 0.79 },
    ads: { in: 0.31, out: 0.25, fovScale: 0.33, sensScale: 0.44 },
    spread: {
      ads: 0, adsMoving: 0.0060, hip: 0.062, hipMoving: 0.098, hipSprint: 0.160,
      crouchScale: 0.68, airScale: 2.8, maxBloom: 0.034, bloomDecay: 0.023, bloomGain: 0.0034,
    },
    recoil: { random: 0.11, kick: 0.26, recovery: 6.6, adsScale: 0.72, crouchScale: 0.80 },
    muzzleVelocity: 790, penetration: 1.9,
    ergonomics: { sprintOut: 0.30, swayScale: 1.35, adsMoveScale: 0.70, switchIn: 0.60, switchOut: 0.38, inspect: 3.0 },
    viewKick: { back: 0.048, up: 0.105, roll: 0.024, side: 0.006, snap: 46, damp: 11 },
    boltTravel: 0.042, shell: { size: 1.2, speed: 3.6 },
    audio: { fire: 'fire_dmr', tail: 'tail_dmr', dry: 'dryfire' },
    modelFallback: 'dmr',
  },
  sr: {
    fireMode: 'bolt', pellets: 1, chambered: true, projectile: true,
    reload: { tactical: 3.10, empty: 3.90, magOut: 0.32, magIn: 0.60, boltRelease: 0.82 },
    ads: { in: 0.52, out: 0.34, fovScale: 0.175, sensScale: 0.24 },
    spread: {
      ads: 0, adsMoving: 0.0100, hip: 0.085, hipMoving: 0.130, hipSprint: 0.190,
      crouchScale: 0.62, airScale: 3.4, maxBloom: 0, bloomDecay: 0.2, bloomGain: 0,
    },
    recoil: { random: 0.08, kick: 0.42, recovery: 5.0, adsScale: 0.70, crouchScale: 0.78 },
    muzzleVelocity: 860, penetration: 3.2,
    ergonomics: { sprintOut: 0.40, swayScale: 1.90, adsMoveScale: 0.55, switchIn: 0.78, switchOut: 0.48, inspect: 3.4 },
    viewKick: { back: 0.075, up: 0.150, roll: 0.020, side: 0.005, snap: 38, damp: 9 },
    boltTravel: 0.058, shell: { size: 1.3, speed: 3.0, delay: 0.3 },
    // Idle sway and the hold-breath that steadies it. Only the scoped weapons get
    // one; `WeaponSystem` treats a weapon without `aimSway` as perfectly steady.
    aimSway: {
      amp: 0.0125, yawScale: 1.45, freq: 0.23, holdScale: 0.06, holdIn: 0.22,
      holdTime: 3.6, recover: 0.55, gaspScale: 1.9, gaspTime: 1.1,
    },
    audio: { fire: 'fire_dmr', tail: 'tail_dmr', dry: 'dryfire', pitchBase: 0.80 },
    modelFallback: 'sniper',
  },
};

/**
 * Per-weapon authored overrides.
 *
 * `climb30Deg` is how far the muzzle should walk over the first 30 rounds of hip
 * fire (or over the whole pattern table, for the weapons with fewer than 30 entries
 * in it), in degrees. `weapons.data.js` divides it by the weapon's own pattern sum to get
 * `recoil.pitch`, so retuning a pattern's shape cannot silently change how hard the
 * weapon kicks — the two are separate decisions and stay separate. `yawRatio` is
 * yaw magnitude as a fraction of pitch.
 *
 * `rpmAuthored` marks the five weapons whose published TTK is a one-shot kill and
 * therefore says nothing about fire rate. Those numbers are picked for the class,
 * not measured, and are the least trustworthy things in this file.
 */
export const AUTHORED = {
  /* --------------------------------- rifles --------------------------------- */
  ak27: { climb30Deg: 20.5, yawRatio: 0.42, random: 0.16, ads: { in: 0.235 }, muzzleVelocity: 880, penetration: 0.95 },
  x9maverick: {
    // One burst is 102 damage against 100 hp: this kills in exactly one trigger
    // pull, which is what its 0.18 s bodyshot TTK is describing.
    fireMode: 'burst', burstCount: 3, burstDelay: 0.20,
    climb30Deg: 17.0, yawRatio: 0.38, random: 0.13, ads: { in: 0.215 }, muzzleVelocity: 900, penetration: 1.00,
  },
  ds20mirage: { climb30Deg: 21.0, yawRatio: 0.44, random: 0.15, ads: { in: 0.245 }, muzzleVelocity: 870, penetration: 0.95 },
  peacekeepermk1: { climb30Deg: 23.0, yawRatio: 0.40, random: 0.17, ads: { in: 0.225 }, muzzleVelocity: 840, penetration: 0.85 },
  m15mod0: { climb30Deg: 19.0, yawRatio: 0.43, random: 0.15, ads: { in: 0.240 }, muzzleVelocity: 860, penetration: 0.90 },
  mxr17: {
    // 487 RPM and 33 damage: the slowest, hardest-hitting rifle on the roster, so
    // it gets the most climb per round and the deepest penetration of the class.
    climb30Deg: 25.0, yawRatio: 0.36, random: 0.12, ads: { in: 0.275 }, muzzleVelocity: 920, penetration: 1.20,
    reload: { tactical: 2.25, empty: 3.00 },
  },

  /* ---------------------------------- SMGs ---------------------------------- */
  razor9mm: { climb30Deg: 15.0, yawRatio: 1.25, random: 0.22, ads: { in: 0.175 }, muzzleVelocity: 380, penetration: 0.35 },
  mpc25: {
    fireMode: 'burst', burstCount: 4, burstDelay: 0.18,
    climb30Deg: 16.0, yawRatio: 1.15, random: 0.20, ads: { in: 0.180 }, muzzleVelocity: 420, penetration: 0.50,
  },
  rk9: { climb30Deg: 17.5, yawRatio: 1.00, random: 0.18, ads: { in: 0.200 }, muzzleVelocity: 430, penetration: 0.55 },
  dravec45: { climb30Deg: 16.5, yawRatio: 1.20, random: 0.20, ads: { in: 0.190 }, muzzleVelocity: 400, penetration: 0.45, reload: { tactical: 1.85, empty: 2.48 } },
  ryden45k: { climb30Deg: 15.5, yawRatio: 1.30, random: 0.21, ads: { in: 0.180 }, muzzleVelocity: 390, penetration: 0.40 },
  carbon57: {
    // 50 rounds of belt-fed pistol calibre: heavier than the class, so it reloads
    // and shoulders slower and braces better.
    climb30Deg: 16.0, yawRatio: 1.10, random: 0.19, ads: { in: 0.205 }, muzzleVelocity: 410, penetration: 0.48,
    reload: { tactical: 2.10, empty: 2.80 }, ergonomics: { sprintOut: 0.18, adsMoveScale: 0.88 },
  },

  /* ---------------------------------- LMGs ---------------------------------- */
  xm325: {
    climb30Deg: 14.5, yawRatio: 0.60, random: 0.15, ads: { in: 0.40 }, muzzleVelocity: 900, penetration: 1.45,
    reload: { tactical: 4.10, empty: 5.05 },
  },
  mk78: {
    climb30Deg: 16.5, yawRatio: 0.55, random: 0.14, ads: { in: 0.46 }, muzzleVelocity: 930, penetration: 1.65,
    reload: { tactical: 4.70, empty: 5.80 },
  },

  /* -------------------------------- shotguns -------------------------------- */
  /**
   * `pellets` is authored and it is load-bearing: it is what turns the published
   * per-pellet damage into a shots-to-kill, and therefore what the derived fire
   * rate is computed against. Each count is the smallest sensible one that
   * reproduces the published TTK.
   *
   * `pelletSpread` is the shell's own pattern, in radians of cone half-angle. It is
   * added to the aim cone inside `Ballistics.fire`, which keeps the aim cone itself
   * exactly zero when shouldered and stationary.
   */
  m10breacher: {
    // 8 x 20 = 160 at contact: the one-shot kill its 0/0 TTK is claiming. Still one
    // shot at 8-17 m (136) and two beyond that (80).
    pellets: 8, pelletSpread: 0.030, rpmAuthored: 80, pumpTime: 0.58,
    climb30Deg: 38.4, yawRatio: 0.26, random: 0.13, muzzleVelocity: 400, penetration: 0.30,
  },
  echo12: {
    // 8 x 11 = 88: two shells, 0.8 s apart, exactly as published. The long reach is
    // what it trades the one-shot for — 88 damage all the way out to 21 m.
    pellets: 8, pelletSpread: 0.024, pumpTime: 0.72,
    climb30Deg: 32.3, yawRatio: 0.30, random: 0.14, muzzleVelocity: 420, penetration: 0.35,
    ads: { in: 0.26 },
  },
  akita: {
    // 10 x 6 = 60: two shells at 200 RPM. A box-magazine semi-auto rather than a
    // tube gun, which is why it is the one shotgun that does not reload shell by
    // shell, and why its damage falls off a cliff by 12 m.
    pellets: 10, pelletSpread: 0.038, fireMode: 'semi', shellByShell: false,
    reload: { tactical: 2.20, empty: 2.95, magOut: 0.30, magIn: 0.60, boltRelease: 0.80 },
    climb30Deg: 16.0, yawRatio: 0.34, random: 0.14, muzzleVelocity: 380, penetration: 0.28,
    ads: { in: 0.21 }, ergonomics: { sprintOut: 0.20, switchIn: 0.46 },
  },

  /* ----------------------------- marksman rifles ---------------------------- */
  warden308: {
    // 75 damage is two body shots against 100 hp, but the page prints a one-shot
    // TTK. `hitboxScale.chest` 1.40 puts a centre-mass hit at 105 and reconciles
    // the two without touching the scraped damage figure; a stomach hit is still
    // 71 and still takes two. Fire rate is authored — a one-shot TTK carries none.
    fireMode: 'semi', rpmAuthored: 300, oneShot: true,
    hitboxScale: { chest: 1.40, stomach: 0.95, arms: 0.62, legs: 0.62 },
    climb30Deg: 44.8, yawRatio: 0.30, random: 0.10, muzzleVelocity: 820, penetration: 2.4,
    ads: { in: 0.34, fovScale: 0.30, sensScale: 0.40 },
    aimSway: { amp: 0.0085, yawScale: 1.3, freq: 0.26, holdScale: 0.08, holdIn: 0.20, holdTime: 4.0, recover: 0.6, gaspScale: 1.7, gaspTime: 0.9 },
    reload: { tactical: 2.55, empty: 3.30 },
  },
  m8a1: {
    // A 4-round burst at 947 RPM in-burst — 120 damage a pull, one burst one kill,
    // which is exactly the 0.19 s the page publishes.
    fireMode: 'burst', burstCount: 4, burstDelay: 0.22,
    climb30Deg: 17.0, yawRatio: 0.34, random: 0.12, muzzleVelocity: 800, penetration: 1.7,
    ads: { in: 0.26, fovScale: 0.42, sensScale: 0.52 }, projectile: false,
    recoil: { kick: 0.19, recovery: 7.4 },
  },
  m34novaline: {
    // Two rounds, 50 each, exactly 100: a double-tap rifle. 546 RPM in-burst.
    fireMode: 'burst', burstCount: 2, burstDelay: 0.26,
    // 1.9, not 2.1. `Ballistics` charges `PENETRATION_COST.flesh * 0.35` to pass
    // through a body, which is 2.0999999999999996 in floating point — so 2.1 sits a
    // rounding error on the wrong side of the threshold and this rifle shot *through*
    // its target and hit it a second time. Measured in play, not reasoned about:
    // a headshot did 123.8 damage instead of 75. Anything below the flesh cost stops
    // in the body, which is what a mid-tier marksman rifle should do; over-penetration
    // is the snipers' trick and they keep it.
    climb30Deg: 25.6, yawRatio: 0.28, random: 0.10, muzzleVelocity: 840, penetration: 1.9,
    ads: { in: 0.33 },
  },

  /* ------------------------------ sniper rifles ----------------------------- */
  vsrecon: {
    rpmAuthored: 50, boltCycle: 1.15, oneShot: true,
    hitboxScale: { chest: 1.00, stomach: 0.72, arms: 0.46, legs: 0.46 },
    climb30Deg: 36.0, yawRatio: 0.29, random: 0.08, muzzleVelocity: 860, penetration: 3.2,
  },
  shadowsk: {
    // Ten rounds and the fastest bolt of the three; the trade is the shortest reach
    // and the least glass.
    rpmAuthored: 56, boltCycle: 1.02, oneShot: true,
    hitboxScale: { chest: 1.00, stomach: 0.72, arms: 0.46, legs: 0.46 },
    climb30Deg: 32.4, yawRatio: 0.32, random: 0.09, muzzleVelocity: 840, penetration: 2.9,
    ads: { in: 0.46, fovScale: 0.20, sensScale: 0.27 }, reload: { tactical: 3.30, empty: 4.15 },
  },
  xr3ion: {
    // 90 damage is not a one-shot on its own; `hitboxScale.chest` 1.15 makes centre
    // mass 103.5 and matches the published one-shot body TTK. Flat to 102 m and
    // beyond, the longest reach on the roster, paid for with the slowest bolt.
    rpmAuthored: 44, boltCycle: 1.30, oneShot: true,
    hitboxScale: { chest: 1.15, stomach: 0.70, arms: 0.44, legs: 0.44 },
    climb30Deg: 39.6, yawRatio: 0.26, random: 0.08, muzzleVelocity: 950, penetration: 3.6,
    ads: { in: 0.58, fovScale: 0.155, sensScale: 0.21 }, reload: { tactical: 3.40, empty: 4.30 },
  },
};

/**
 * Reserve ammunition.
 *
 * The reference publishes マガジン数 but not a reserve count, and does not say
 * whether the loaded magazine is included. Read here as *spare* magazines, so a
 * weapon deploys with a full magazine plus that many more, and can pick up two
 * beyond it. Authored interpretation, not a scraped number.
 */
export function reserveFor(magSize, magazines) {
  return { startReserve: magSize * magazines, maxReserve: magSize * (magazines + 2) };
}

/* -------------------------------------------------------------------------- */
/* DERIVED — computed here, never typed in                                     */
/* -------------------------------------------------------------------------- */

/**
 * Everything the scraped table implies, worked out at module load.
 *
 * Computed last because the shotguns' shots-to-kill depends on an authored pellet
 * count: the reference publishes shotgun damage per pellet, so `100 / damage` is
 * meaningless until you know how many of them are in the shell.
 *
 * `rpm` is null exactly when the published TTK carries no rate information — a
 * one-shot kill, or a TTK of zero. Those five weapons fall back to
 * `AUTHORED[id].rpmAuthored`, and `rpmIsAuthored` says so.
 */
export const DERIVED = {};
for (const [id, s] of Object.entries(SCRAPED)) {
  const a = AUTHORED[id] ?? {};
  const pellets = a.pellets ?? 1;
  const perShot = s.bands[0][1] * pellets;
  // `oneShot` weapons are excluded by hand rather than by the shots-to-kill test.
  // XR-3 Ion is why: 90 damage is two shots on the arithmetic, but its published
  // 0.05 s body TTK says one, and `hitboxScale.chest` is what reconciles them. Left
  // to the formula it would derive 1200 RPM out of a bolt-action.
  const rpm = a.oneShot ? null : deriveRpm(perShot, s.ttk.body);
  DERIVED[id] = {
    /** Damage a single trigger pull does at contact range, all pellets counted. */
    damagePerShot: perShot,
    shotsToKill: shotsToKill(perShot),
    rpm: rpm ?? a.rpmAuthored ?? 600,
    rpmIsAuthored: rpm === null,
    /** The published bands, reshaped for the engine's linear interpolator. */
    curve: bandsToCurve(s.bands),
    /** Effective headshot multiplier once the x3.0 head hitbox is accounted for. */
    headHitboxScale: headHitboxScale(id),
    ...reserveFor(s.magSize, s.magazines),
  };
}

/** Ids in roster order: class by class, best rank first inside each class. */
export const ROSTER = Object.keys(SCRAPED);
